/**
 * @file DigestNotificationProvider
 * @description App-level React context provider that manages background digest
 * generation. Fires toast notifications on completion/error and renders a
 * DigestViewerModal when the user clicks "View Digest" on the toast.
 */

import { useGenerateDigest } from '@medsuite/api-client';
import { useToast } from '@medsuite/ui-components';
import { createContext, useContext, useState, useCallback, useMemo } from 'react';

import { DigestViewerModal } from '../components/DigestViewerModal';

interface DigestNotificationContextType {
  startGeneration: () => void;
  isGenerating: boolean;
}

const DigestNotificationContext = createContext<DigestNotificationContextType | undefined>(undefined);

export function DigestNotificationProvider({ children }: { children: React.ReactNode }) {
  const { addToast } = useToast();
  const [viewDigestId, setViewDigestId] = useState<number | null>(null);

  const generateMutation = useGenerateDigest({
    onSuccess: (data) => {
      addToast({
        title: 'Digest Ready',
        message: 'Your daily digest has been generated successfully.',
        variant: 'success',
        duration: 10000,
        action: {
          label: 'View Digest',
          onClick: () => setViewDigestId(data.digest_id),
        },
      });
    },
    onError: (error) => {
      addToast({
        title: 'Digest Failed',
        message: error.message || 'Failed to generate digest.',
        variant: 'error',
      });
    },
  });

  const startGeneration = useCallback(() => {
    generateMutation.mutate({ format: 'full' });
  }, [generateMutation.mutate]);

  const contextValue = useMemo(
    () => ({ startGeneration, isGenerating: generateMutation.isPending }),
    [startGeneration, generateMutation.isPending]
  );

  return (
    <DigestNotificationContext.Provider value={contextValue}>
      {children}
      <DigestViewerModal
        isOpen={viewDigestId != null}
        onClose={() => setViewDigestId(null)}
        digestId={viewDigestId}
      />
    </DigestNotificationContext.Provider>
  );
}

export function useDigestNotification() {
  const context = useContext(DigestNotificationContext);
  if (!context) {
    throw new Error('useDigestNotification must be used within a DigestNotificationProvider');
  }
  return context;
}
